import { store } from '../store';
import {
  setLoading,
  setError,
  setLogs,
  addLog,
  updateLog,
  removeLog,
  setSubjects,
  addSubject,
  updateSubject,
  removeSubject,
} from '../store/slices/logSlice';
import type { Log, Subject, CreateLogInput, UpdateLogInput, CreateSubjectInput, UpdateSubjectInput, LogQuery } from '../types/log';
import { getTransport } from '../transport';

class LogService {
  private cleanupFns: (() => void)[] = [];
  private initialized = false;

  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    this.setupListeners();
    await this.loadLogs();
    await this.loadSubjects();
  }

  destroy(): void {
    this.cleanupFns.forEach((fn) => fn());
    this.cleanupFns = [];
    this.initialized = false;
  }

  private setupListeners(): void {
  }

  async loadLogs(query?: LogQuery): Promise<void> {
    const transport = getTransport();
    if (!transport?.listLogs) {
      console.warn('[LogService] No transport available');
      return;
    }

    store.dispatch(setLoading(true));
    try {
      const result = await transport.listLogs(query);
      if (result.success && result.logs) {
        store.dispatch(setLogs(result.logs));
      } else {
        throw new Error(result.error || 'Failed to load logs');
      }
    } catch (err: unknown) {
      console.error('[LogService] Error loading logs:', err);
      store.dispatch(setError(err instanceof Error ? err.message : String(err)));
    }
  }

  async createLog(input: CreateLogInput): Promise<Log | null> {
    const transport = getTransport();
    if (!transport?.createLog) return null;

    try {
      const result = await transport.createLog(input);
      if (result.success && result.log) {
        store.dispatch(addLog(result.log));
        return result.log;
      } else {
        throw new Error(result.error || 'Failed to create log');
      }
    } catch (err: unknown) {
      store.dispatch(setError(err instanceof Error ? err.message : String(err)));
      throw err;
    }
  }

  async updateLog(id: string, input: UpdateLogInput): Promise<Log | null> {
    const transport = getTransport();
    if (!transport?.updateLog) return null;

    try {
      const result = await transport.updateLog(id, input);
      if (result.success && result.log) {
        store.dispatch(updateLog(result.log));
        return result.log;
      } else {
        throw new Error(result.error || 'Failed to update log');
      }
    } catch (err: unknown) {
      store.dispatch(setError(err instanceof Error ? err.message : String(err)));
      throw err;
    }
  }

  async deleteLog(id: string): Promise<void> {
    const transport = getTransport();
    if (!transport?.deleteLog) {
      throw new Error('No transport available');
    }

    try {
      const result = await transport.deleteLog(id);
      if (result.success) {
        store.dispatch(removeLog(id));
      } else {
        throw new Error(result.error || 'Failed to delete log');
      }
    } catch (err: unknown) {
      store.dispatch(setError(err instanceof Error ? err.message : String(err)));
      throw err;
    }
  }

  async getLog(id: string): Promise<Log | null> {
    const transport = getTransport();
    if (!transport?.getLog) return null;

    try {
      const result = await transport.getLog(id);
      if (result.success && result.log) {
        return result.log;
      }
      return null;
    } catch (err: unknown) {
      store.dispatch(setError(err instanceof Error ? err.message : String(err)));
      return null;
    }
  }

  async getLogSubjects(logId: string): Promise<Subject[]> {
    const transport = getTransport();
    if (!transport?.getLogSubjects) return [];

    try {
      const result = await transport.getLogSubjects(logId);
      if (result.success && result.subjects) {
        return result.subjects;
      }
      return [];
    } catch (err) {
      console.error('Error loading log subjects:', err);
      return [];
    }
  }

  async addSubjectToLog(logId: string, subjectId: string): Promise<boolean> {
    const transport = getTransport();
    if (!transport?.addSubjectToLog) return false;

    try {
      const result = await transport.addSubjectToLog(logId, subjectId);
      return result.success && (result.result ?? false);
    } catch (err) {
      console.error('Error adding subject to log:', err);
      return false;
    }
  }

  async removeSubjectFromLog(logId: string, subjectId: string): Promise<boolean> {
    const transport = getTransport();
    if (!transport?.removeSubjectFromLog) return false;

    try {
      const result = await transport.removeSubjectFromLog(logId, subjectId);
      return result.success && (result.result ?? false);
    } catch (err) {
      console.error('Error removing subject from log:', err);
      return false;
    }
  }

  async loadSubjects(userId?: string): Promise<Subject[]> {
    const transport = getTransport();
    if (!transport?.listSubjects) return [];

    try {
      const result = await transport.listSubjects(userId);
      if (result.success && result.subjects) {
        store.dispatch(setSubjects(result.subjects));
        return result.subjects;
      }
      return [];
    } catch (err) {
      console.error('[LogService] Error loading subjects:', err);
      return [];
    }
  }

  async createSubject(input: CreateSubjectInput): Promise<Subject | null> {
    const transport = getTransport();
    if (!transport?.createSubject) return null;

    try {
      const result = await transport.createSubject(input);
      if (result.success && result.subject) {
        store.dispatch(addSubject(result.subject));
        return result.subject;
      }
      return null;
    } catch (err) {
      console.error('Error creating subject:', err);
      return null;
    }
  }

  async updateSubject(id: string, input: UpdateSubjectInput): Promise<Subject | null> {
    const transport = getTransport();
    if (!transport?.updateSubject) return null;

    try {
      const result = await transport.updateSubject(id, input);
      if (result.success && result.subject) {
        store.dispatch(updateSubject(result.subject));
        return result.subject;
      }
      return null;
    } catch (err) {
      console.error('Error updating subject:', err);
      return null;
    }
  }

  async deleteSubject(id: string): Promise<boolean> {
    const transport = getTransport();
    if (!transport?.deleteSubject) return false;

    try {
      const result = await transport.deleteSubject(id);
      if (result.success) {
        store.dispatch(removeSubject(id));
      }
      return result.success;
    } catch (err) {
      console.error('Error deleting subject:', err);
      return false;
    }
  }

  async getSubject(id: string): Promise<Subject | null> {
    const transport = getTransport();
    if (!transport?.getSubject) return null;

    try {
      const result = await transport.getSubject(id);
      if (result.success && result.subject) {
        return result.subject;
      }
      return null;
    } catch (err: unknown) {
      return null;
    }
  }

  async getSubjectLogs(subjectId: string): Promise<Log[]> {
    const transport = getTransport();
    if (!transport?.getSubjectLogs) return [];

    try {
      const result = await transport.getSubjectLogs(subjectId);
      if (result.success && result.logs) {
        return result.logs;
      }
      return [];
    } catch (err) {
      console.error('Error loading subject logs:', err);
      return [];
    }
  }
}

export const logService = new LogService();